import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ShieldCheck, ShieldAlert, Loader2, Lock, FileText, Eye, Trash2, Bell } from 'lucide-react';

export default function PatientAIConsent() {
  const [consent, setConsent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/ai/records/me/consent');
      setConsent(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load your consent status.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const update = async (granted) => {
    setSaving(true);
    try {
      const res = await api.post('/ai/records/me/consent', { granted });
      setConsent(res.data);
      toast.success(granted ? 'AI consent granted.' : 'AI consent revoked.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update your consent.');
    } finally {
      setSaving(false);
    }
  };

  const granted = consent?.granted;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white">AI &amp; Privacy</h2>
        <p className="text-sm text-slate-500">Control whether our AI tools may use your medical records to support your care team.</p>
      </div>

      {/* Pending request nudge */}
      {consent?.has_pending_request && !consent?.granted && (
        <div className="rounded-xl border-2 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center shrink-0">
            <Bell className="w-4 h-4 text-amber-600" />
          </div>
          <div>
            <div className="font-semibold text-amber-800 dark:text-amber-300 text-sm">Your care team has requested AI consent</div>
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
              {consent.pending_request_from ? `${consent.pending_request_from} ` : 'A member of your care team '}
              would like to use AI to help prepare for your care. You can grant or decline below.
            </p>
          </div>
        </div>
      )}

      {/* Consent status card */}
      <Card>
        <CardContent className="p-6">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${granted ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-slate-100 dark:bg-slate-800'}`}>
                  {granted ? <ShieldCheck className="w-6 h-6 text-emerald-600" /> : <ShieldAlert className="w-6 h-6 text-slate-500" />}
                </div>
                <div>
                  <div className="font-semibold text-slate-900 dark:text-white">
                    {granted ? 'AI assistance is ON' : 'AI assistance is OFF'}
                  </div>
                  <p className="text-sm text-slate-500 max-w-md">
                    {granted
                      ? 'Your care team can use AI to summarise and search your uploaded records to help prepare for consultations.'
                      : 'Your records will not be processed by any AI feature until you turn this on.'}
                  </p>
                  {consent?.updated_at && (
                    <p className="text-[11px] text-slate-400 mt-1">Last updated: {String(consent.updated_at).slice(0, 19).replace('T', ' ')}</p>
                  )}
                </div>
              </div>
              <div>
                {granted ? (
                  <Button onClick={() => update(false)} disabled={saving} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    Revoke consent
                  </Button>
                ) : (
                  <Button onClick={() => update(true)} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                    Grant consent
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* What this means */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">What granting consent allows</h3>
          {[
            { icon: FileText, title: 'Record summaries', desc: 'AI prepares a concise, cited summary of your uploaded records for your doctor.' },
            { icon: Eye, title: 'Faster consultations', desc: 'Your doctor can quickly ask questions about your history to save time in appointments.' },
            { icon: Lock, title: 'You stay in control', desc: 'You can revoke consent at any time. AI never diagnoses — your doctor makes all decisions.' },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center shrink-0">
                <item.icon className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{item.title}</div>
                <div className="text-xs text-slate-500">{item.desc}</div>
              </div>
            </div>
          ))}
          <div className="flex items-start gap-2 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-3 mt-2">
            <ShieldCheck className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
            <p className="text-[11px] text-slate-500">
              Your records are stored securely and processed only for your care. AI output is decision-support for your medical team and is never a diagnosis.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
